import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useAudioPlayback = (podcastId, token) => {
  const [podcast, setPodcast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [activeSpeaker, setActiveSpeaker] = useState(null);
  const [segmentTimings, setSegmentTimings] = useState([]);
  const [revealed, setRevealed] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const audioRef = useRef(null);

  useEffect(() => {
    const fetchPodcast = async () => {
      try {
        const response = await axios.get(`${API}/podcasts/${podcastId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPodcast(response.data.podcast);
        setSegmentTimings(response.data.podcast.segment_timings || []);
        document.title = `${response.data.podcast.topic} | Opposite Direction Podcast`;

        const favResponse = await axios.get(`${API}/favorites`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(favResponse.data.favorites.some(f => f.podcast_id === podcastId));

        if (response.data.podcast.audio_path) {
          try {
            const audioRes = await fetch(`${API}/podcasts/${podcastId}/audio`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (audioRes.ok) {
              const blob = await audioRes.blob();
              if (blob.size > 0) {
                setAudioUrl(URL.createObjectURL(blob));
              }
            }
          } catch (e) { console.error('Audio fetch failed:', e); }
        }

        try {
          const quotesRes = await axios.get(`${API}/podcasts/${podcastId}/quotes`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setQuotes(quotesRes.data.quotes || []);
        } catch (e) { console.error('Quotes fetch failed:', e); }
      } catch {
        toast.error('Failed to load podcast');
      } finally {
        setLoading(false);
      }
    };
    fetchPodcast();
    return () => { if (audioUrl) URL.revokeObjectURL(audioUrl); };
  }, [podcastId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Active speaker tracking
  useEffect(() => {
    if (!isPlaying || segmentTimings.length === 0) {
      if (!isPlaying) setActiveSpeaker(null);
      return;
    }
    const ms = currentTime * 1000;
    const seg = segmentTimings.find(s => ms >= s.start_ms && ms < s.end_ms);
    setActiveSpeaker(seg ? seg.expert_id : null);
  }, [currentTime, isPlaying, segmentTimings]);

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => toast.error('Playback failed'));
      if (!revealed) setRevealed(true);
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying, revealed]);

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) setDuration(audioRef.current.duration);
  }, []);

  const handleSeek = useCallback((e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = x * duration;
    setCurrentTime(audioRef.current.currentTime);
  }, [duration]);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setActiveSpeaker(null);
  }, []);

  const toggleFavorite = useCallback(async () => {
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${podcastId}`, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/favorites`, { podcast_id: podcastId }, { headers: { Authorization: `Bearer ${token}` } });
      }
      setIsFavorite(!isFavorite);
      toast.success(isFavorite ? 'Removed from favorites' : 'Added to favorites');
    } catch { toast.error('Failed to update favorites'); }
  }, [isFavorite, podcastId, token]);

  const handleShare = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied!');
    } catch { toast.error('Failed to copy'); }
  }, []);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return {
    podcast, loading, isPlaying, currentTime, duration, isFavorite, audioUrl,
    activeSpeaker, segmentTimings, revealed, quotes, audioRef, progress,
    togglePlay, handleTimeUpdate, handleLoadedMetadata, handleSeek, handleEnded,
    toggleFavorite, handleShare
  };
};
